import {
  LlmToolPublic,
  TrimSelectorRequest,
  TrimSelectorResponse,
} from '@fullstory/proto/fs/services/llm-tool';
import { getDefaultHeaders } from '@fullstory/util/fetch';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';
import { createRequestCancellationAction } from '@fullstory/redux-util';
import { AppDispatch, LlmToolRequestActions } from '@fullstory/redux';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

type OptimizeSelectorReturn = {
  selector: string;
  error?: string;
};

export const useBackendSelectorTrim = () => {
  const dispatch = useDispatch<AppDispatch>();

  const trim = useCallback(
    async (
      orgId: string,
      selector: string,
      platforms: NativePlatform[],
      abortController?: AbortController,
    ): Promise<OptimizeSelectorReturn> => {
      const req: TrimSelectorRequest = { orgId, selector, nativePlatforms: platforms };

      try {
        const trimSelectorAction = LlmToolRequestActions.trimSelector(req, true);
        const promises = [dispatch(trimSelectorAction)];

        if (abortController) {
          promises.push(
            new Promise<OptimizeSelectorReturn>(resolve => {
              abortController.signal.addEventListener('abort', () => {
                dispatch(createRequestCancellationAction(trimSelectorAction.id));
                resolve({ selector });
              });
            }),
          );
        }

        return Promise.race(promises);
      } catch (e) {
        console.error('error trimming selector', e);
        return { selector };
      }
    },
    [dispatch],
  );

  return {
    trim,
  };
};

/**
 * @deprecated Streaming does not work in GCP environments. Use `useBackendSelectorTrim` instead.
 */
export async function trimSelectorBackend(
  orgId: string,
  selector: string,
  platforms: NativePlatform[],
  abortController: AbortController,
  onPartial: (partiallyOptimizedSelector: string) => void,
): Promise<OptimizeSelectorReturn> {
  const readingTrimmedSelectorsError = 'failed reading trimmed selectors';
  let lastSelector = selector;

  try {
    const req: TrimSelectorRequest = { orgId, selector, nativePlatforms: platforms };
    const resp = await fetchStreaming<TrimSelectorResponse>(
      `${LlmToolPublic.TrimSelectorEndpoint}Proxy`,
      {
        signal: abortController.signal,
        body: JSON.stringify(req),
      },
    );

    for await (const result of resp) {
      if (result.data) {
        lastSelector = result.data.selector;
        onPartial(lastSelector);
      } else {
        console.error(readingTrimmedSelectorsError, result.error);
      }
    }
  } catch (e) {
    console.error(readingTrimmedSelectorsError, e);
  }

  return { selector: lastSelector };
}

/**
 * fetchStreaming fetches a streaming response from a gRPC streaming endpoint that has been proxied
 * to HTTP streaming by explicitly registering a server-side handler that responds with JSON seq
 * messages.
 *
 * @deprecated Streaming does not work in GCP environments.
 *
 * @param input request target, similar to fetch
 * @param init request options, similar to fetch
 * @returns an async generator that yields JSON seq values from the response
 */
export async function fetchStreaming<TResp>(input: RequestInfo, init?: RequestInit) {
  const _init = init || {};
  _init.method = _init.method || 'POST';
  _init.headers = _init.headers || getDefaultHeaders();

  const resp = await fetch(input, _init);
  if (resp.status < 200 || resp.status > 299) {
    throw new Error('error fetching streaming endpoint');
  }

  return generateJsonSeqValues<TResp>(resp, _init?.signal || undefined);
}

/**
 * generateJsonSeqValues is an async generator that yields TResp values parsed from JSON seq messages
 * streamed in the response body.

 * @deprecated Streaming does not work in GCP environments.
 *
 * @param resp the streaming respose
 * @param signal an optional signal to abort the request
 * @yields TResp values parsed from JSON seq messages in the response body stream
 */
async function* generateJsonSeqValues<TResp>(
  resp: Response,
  signal?: AbortSignal,
): AsyncGenerator<StreamingResponse<TResp>> {
  const reader = resp.body?.getReader();
  if (!reader || resp.status < 200 || resp.status > 299) {
    return;
  }

  const encoder = new TextEncoder();
  const decoder = new TextDecoder('utf-8');
  const recordSeparatorCode = 0x1e; // ascii record separator per json-seq rfc
  const recordSeparatorString = String.fromCharCode(recordSeparatorCode);
  const newlineString = String.fromCharCode(0x0a);
  const delimiter = `${newlineString}${recordSeparatorString}`;

  // JSON messages may be split across multiple message chunks. If we encounter an incomplete JSON message
  // because it's split across message chunks, we need to buffer the message to be combined with chunks from
  // subsequent stream reads.
  let buffer = new Uint8Array(0);

  for (let { done, value } = await reader.read(); !done; { done, value } = await reader.read()) {
    if (signal?.aborted) {
      await reader.cancel();
      return;
    }

    if (!value) {
      continue;
    }

    // Combine any previously buffered partial messages with messages from the current stream read.
    const combined = new Uint8Array(buffer.length + value.length);
    combined.set(buffer, 0);
    combined.set(value, buffer.length);

    const text = decoder.decode(combined, { stream: true });
    const messages = text.split(delimiter);

    // JSON could contain new lines. We won't know the last message is complete JSON until we either
    // encounter a record separator prefixing the next message or we reach the end of the stream.
    const lastMessage = messages.pop();
    if (!lastMessage) {
      // We didn't have any messages after splitting on the delimiter, so we retain the combined buffer.
      buffer = combined;
      continue;
    }

    // If we had messages to process after splitting on the delimiter, we'll carry forward a buffer with
    // the last message only.
    buffer = encoder.encode(lastMessage);

    for (const message of messages) {
      const result: StreamingResponse<TResp> = JSON.parse(
        message.replace(recordSeparatorString, ''),
      );

      if (result.data) {
        yield {
          data: result.data,
        };
      } else {
        throw new Error(result.error);
      }
    }
  }

  // When we're done reading the stream, parse and return any remaining buffered messages.
  if (buffer.length) {
    const text = decoder.decode(buffer, { stream: true });
    const messages = text.split(delimiter);

    for (const message of messages) {
      const result: StreamingResponse<TResp> = JSON.parse(
        message.replace(recordSeparatorString, ''),
      );

      if (result.data) {
        yield {
          data: result.data,
        };
      } else {
        throw new Error(result.error);
      }
    }
  }
}

/**
 * StreamingResponse is the expected response body payload returned from streaming endpoints registered
 * via our server-side HTTP handler which reads from gRPC streams and writes to HTTP response bodies.
 */
export type StreamingResponse<TResp> =
  | {
      data: TResp;
      error?: never;
    }
  | {
      data?: never;
      error: string;
    };
